import PageHeader from "@/components/site/PageHeader";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import CTABanner from "@/components/site/CTABanner";
import usePageTitle from "@/lib/usePageTitle";
import Reveal from "@/components/site/Reveal";

const SERVICES = [
  {
    title: "Revenue Leak Audit",
    tagline: "A focused review to find what's quietly costing you.",
    desc: "I review your listing through a strategic, conversion-focused lens and surface the 3–5 most impactful issues — the things that are quietly costing you bookings and revenue.",
    includes: [
      "Listing presentation review (top 3 photos, title, description)",
      "Conversion friction points",
      "Pricing & positioning gaps",
      "Strategic next-step priorities",
      "Concise written report",
    ],
    fit: "Best for owners who want clarity fast without ongoing commitment.",
  },
  {
    title: "Strategy Intensive",
    tagline: "A deep-dive plan customized to your property.",
    desc: "A focused engagement to build a clear, measurable strategy across positioning, pricing, guest experience, and operations — so you stop guessing and start executing with intention.",
    includes: [
      "Full property audit + market lens",
      "Positioning & messaging strategy",
      "Pricing framework & seasonality plan",
      "Guest experience & touchpoint map",
      "Action roadmap with priorities",
    ],
    fit: "Best for owners or investors ready for a strategic reset.",
  },
  {
    title: "Monthly Advisory",
    tagline: "Ongoing strategic support, monthly clarity.",
    desc: "A monthly retainer offering strategic guidance, performance review, and accountability — so you have a calm, intelligent thinking partner in your corner as the business evolves.",
    includes: [
      "Monthly strategy sessions",
      "Performance & trend review",
      "Optimization recommendations",
      "Async strategic support",
      "Quarterly planning",
    ],
    fit: "Best for growth-focused operators and portfolio owners.",
  },
];

export default function ServicesPage() {
  usePageTitle("Services");
  return (
    <div data-testid="page-services" className="bg-ivory">
      <PageHeader
        overline="Services"
        title="Strategic support for"
        accent="every stage."
        description="From quick clarity to long-term advisory — choose the support that fits where you are, and where you want to go."
        testId="services-header"
      />

      <section className="pb-24 lg:pb-32">
        <div className="container-editorial space-y-12 lg:space-y-16">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title}>
              <div
                data-testid={`services-card-${i}`}
                className="bg-cream border border-warmGray/15 p-10 lg:p-16"
              >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
                  <div className="lg:col-span-5">
                    <p className="font-ui uppercase tracking-[0.2em] text-[13px] font-semibold text-sage mb-6">
                      0{i + 1} · Service
                    </p>
                    <h2 className="font-serif text-3xl md:text-4xl lg:text-[2.5rem] text-navy leading-[1.12]">
                      {s.title}
                    </h2>
                    <p className="font-serif italic text-charcoal/75 text-lg md:text-xl mt-4 leading-snug">
                      {s.tagline}
                    </p>
                  </div>
                  <div className="lg:col-span-4">
                    <p className="font-sans text-[15px] text-charcoal/80 leading-[1.75]">{s.desc}</p>
                    <p className="font-sans text-[14px] italic text-charcoal/65 mt-5 leading-relaxed">{s.fit}</p>
                  </div>
                  <div className="lg:col-span-3">
                    <p className="font-ui uppercase tracking-[0.18em] text-[12px] font-semibold text-navy/80 mb-5">
                      What's Included
                    </p>
                    <ul className="space-y-3">
                      {s.includes.map((item, j) => (
                        <li key={j} className="flex gap-2.5 font-sans text-[14px] text-charcoal/85 leading-snug">
                          <Check className="w-4 h-4 text-sage shrink-0 mt-0.5" strokeWidth={1.8} />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/contact"
                      data-testid={`services-inquire-${i}`}
                      className="editorial-link mt-9 inline-flex"
                    >
                      Inquire <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                    </Link>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CTABanner />
    </div>
  );
}
